import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { NursingAllowanceConfirmationWidgetStore } from './nursing-allowance-confirmation-widget.store';
import { nursingAllowanceConfirmationWidgetFragment } from './nursing-allowance-confirmation-widget.query';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { NursingAllowanceCareTypeEnum } from '@libs/types/src/services/enum/nursing-allowance-care-type.enum';
import { NursingAllowanceStateEnum } from '@libs/types/src/services/enum/nursing-allowance-state.enum';
import {
    FolderNursingAllowanceObject,
} from '@libs/types/src/services/object-type/folder/folder-nursing-allowance.object';
import { nursingAllowanceDaysUntilConfirmation } from '@libs/types/src/services/utils/nursing-allowance';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { TableColumn, TableColumnType } from '../../../../../shared/components/kit/table/table.types';
import { clientChipField, serviceField } from '../../../../../shared/components/kit/table/table.fields';
import { sessionService, translate } from '../../../../../shared/shared.global-service';
import { folderUri } from '../../../../../../utils/router.utils';

@Component({
    selector: 'app-nursing-allowance-confirmation-widget',
    templateUrl: './nursing-allowance-confirmation-widget.component.html',
    styleUrl: './nursing-allowance-confirmation-widget.component.scss',
    imports: [TableComponent],
    providers: [NursingAllowanceConfirmationWidgetStore],
})
export class NursingAllowanceConfirmationWidgetComponent {
    protected readonly store = inject(NursingAllowanceConfirmationWidgetStore);
    private readonly router = inject(Router);

    items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);

    protected readonly fragment = nursingAllowanceConfirmationWidgetFragment;
    protected readonly columns: TableColumn[] = [
        clientChipField('client', 'folder.person', false),
        serviceField('service', 'folder.serviceId'),
        {
            name: 'confirmation',
            dataKey: null,
            type: TableColumnType.Tag,
            data: (item: FolderNursingAllowanceObject) => this.confirmationLabel(item),
            severity: (_value, item: FolderNursingAllowanceObject) => {
                const days = this.daysUntilConfirmation(item);
                if (days === null) return 'secondary';
                return days <= 3 ? 'danger' : days <= 7 ? 'warn' : 'success';
            },
        },
    ];

    protected readonly additionalFilter = computed(() => [
        [{ column: 'serviceId', operator: 'IN', value: this.items().map(item => item.serviceId) }],
        [{ column: 'careType', operator: 'EQ', value: [NursingAllowanceCareTypeEnum.LONG_TERM] }],
        [{ column: 'state', operator: 'EQ', value: [NursingAllowanceStateEnum.OPEN] }],
        [{ column: 'activeIcpe', operator: 'EQ', value: ['true'] }],
    ]);

    protected openDetail(item: FolderNursingAllowanceObject): void {
        void this.router.navigateByUrl(folderUri(item.folderId, item.folder?.serviceId, 'nursing-allowance/' + item.id));
    }

    private daysUntilConfirmation(item: FolderNursingAllowanceObject): number | null {
        return nursingAllowanceDaysUntilConfirmation(item.confirmedUntil, item.careStartDate,
            new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Prague' }).format(new Date()));
    }

    private confirmationLabel(item: FolderNursingAllowanceObject): string {
        const days = this.daysUntilConfirmation(item);
        if (days === null) return '—';
        if (days < 0) return translate.instant('nursingAllowance.controlOverdue');
        return new Intl.RelativeTimeFormat(sessionService.locale, { numeric: 'auto' }).format(days, 'day');
    }
}
